'use client';

import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { ShieldCheck, X, FileText, CheckCircle2, Upload, Loader2, Trash2, ExternalLink, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { useToast } from './ui/Toast';

interface KycSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingKyc?: any;
  kycStatus?: string;
}

export default function KycSubmissionModal({
  isOpen,
  onClose,
  onSuccess,
  existingKyc,
  kycStatus,
}: KycSubmissionModalProps) {
  const { showToast } = useToast();
  const [companyName, setCompanyName] = useState('');
  const [teamSize, setTeamSize] = useState('1-10');
  const [idType, setIdType] = useState<string>('GST');
  const [verifiedDomain, setVerifiedDomain] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const hasSubmittedDocument = Boolean(existingKyc?.documentUrl);
  const status = existingKyc?.status || kycStatus;
  const isPendingReview = hasSubmittedDocument && (status === 'PENDING' || kycStatus === 'PENDING');
  const isRejected = hasSubmittedDocument && (status === 'REJECTED' || kycStatus === 'REJECTED');

  useEffect(() => {
    if (existingKyc) {
      setCompanyName(existingKyc.companyName || '');
      setTeamSize(existingKyc.teamSize || '1-10');
      if (existingKyc.idType) setIdType(existingKyc.idType);
      if (existingKyc.verifiedDomain) setVerifiedDomain(existingKyc.verifiedDomain);
      if (existingKyc.documentUrl) setDocumentUrl(existingKyc.documentUrl);
    }
  }, [existingKyc, isOpen]);

  if (!isOpen) return null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      showToast('File size must be under 15MB', 'error');
      return;
    }

    setIsUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/kyc/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data;
      if (data?.url) {
        setDocumentUrl(data.url);
        setUploadedFileName(data.name || file.name);
        showToast('Identity document uploaded successfully!', 'success');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to upload document.');
      showToast(err?.response?.data?.message || 'Failed to upload document.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentUrl) {
      setError('Please upload your identity document file before submitting.');
      showToast('Please upload your identity document file before submitting.', 'error');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await api.post('/kyc/submit', {
        companyName,
        teamSize,
        idType,
        verifiedDomain,
        documentUrl,
      });
      showToast('KYC application submitted successfully! Pending Superadmin review.', 'success');
      setIsEditing(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to submit KYC.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-600/20 border border-indigo-500/30 text-indigo-400 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Organization Identity Verification (KYC)</h3>
            <p className="text-xs text-slate-400">
              {isPendingReview ? 'Application status and submitted documents (View Only)' : 'Upload document to enable workspace & board creation'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs text-rose-400">
            {error}
          </div>
        )}

        {/* 1. READ-ONLY View when KYC is PENDING */}
        {isPendingReview ? (
          <div className="space-y-5">
            <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl flex items-center gap-3">
              <Clock className="w-6 h-6 text-indigo-400 shrink-0" />
              <div>
                <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                  <span>Status: Application Under Review</span>
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-[10px] rounded font-bold">READ ONLY</span>
                </h4>
                <p className="text-xs text-slate-300 mt-0.5">
                  Your identity verification is currently being audited by Superadmin. Modifications are disabled while under review.
                </p>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3 text-xs">
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Company Name</span>
                <span className="font-semibold text-white">{existingKyc?.companyName || companyName}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Team Size</span>
                <span className="font-semibold text-white">{existingKyc?.teamSize || teamSize}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Document Type</span>
                <span className="font-semibold text-white">{existingKyc?.idType || idType}</span>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-slate-400">Submitted Document Attachment</span>
                {existingKyc?.documentUrl || documentUrl ? (
                  <a
                    href={existingKyc?.documentUrl || documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-indigo-300 font-bold text-xs rounded-lg flex items-center gap-1.5 cursor-pointer"
                  >
                    <FileText className="w-3.5 h-3.5" /> View Document <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-slate-500">None</span>
                )}
              </div>
            </div>

            <div className="pt-2 flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl cursor-pointer"
              >
                Close View
              </button>
            </div>
          </div>
        ) : isRejected && !isEditing ? (
          /* 2. REJECTED View with Re-submit / Edit Action */
          <div className="space-y-5">
            <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-bold text-white uppercase tracking-wider">KYC Application Rejected</h4>
                <p className="text-xs text-rose-300 mt-1">
                  Rejection Reason: <strong>{existingKyc?.rejectionReason || 'Uploaded document is unreadable or incomplete.'}</strong>
                </p>
                <p className="text-[11px] text-slate-400 mt-2">
                  Please update your document and re-submit for verification.
                </p>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-rose-600/20 flex items-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Re-submit & Update KYC Document</span>
              </button>
            </div>
          </div>
        ) : (
          /* 3. SUBMISSION / EDIT FORM */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                Company / Organization Name
              </label>
              <input
                type="text"
                placeholder="e.g. Scalesutra Tech Ltd"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                  Team Size
                </label>
                <select
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                >
                  <option value="1-10">1-10 Employees</option>
                  <option value="11-50">11-50 Employees</option>
                  <option value="51-200">51-200 Employees</option>
                  <option value="201+">201+ Employees</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                  Verification Document Type
                </label>
                <select
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                >
                  <option value="GST">GST Certificate / GSTIN</option>
                  <option value="DOMAIN">Domain Ownership / Proof</option>
                  <option value="AADHAAR">Aadhaar Card</option>
                  <option value="NATIONAL_ID_CARD">National ID Card</option>
                  <option value="PASSPORT">Passport</option>
                  <option value="DRIVERS_LICENSE">Driver's License</option>
                  <option value="OTHER">Other Business Document</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                Company Email Domain (Auto-Membership)
              </label>
              <input
                type="text"
                placeholder="e.g. scalesutra.com"
                value={verifiedDomain}
                onChange={(e) => setVerifiedDomain(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Anyone registering with an email matching this domain (e.g., <code>@scalesutra.com</code>) will automatically be verified and added to your workspace.
              </p>
            </div>

            {/* Direct File Upload Section */}
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                Upload Identity Proof Document <span className="text-rose-400">*</span>
              </label>

              {documentUrl ? (
                <div className="p-3 bg-slate-950 border border-emerald-500/30 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <FileText className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="truncate">
                      <p className="text-xs font-medium text-slate-200 truncate">
                        {uploadedFileName || 'Uploaded_Document.pdf'}
                      </p>
                      <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Ready for submission
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={documentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-indigo-400 transition-colors"
                      title="View Document"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        setDocumentUrl('');
                        setUploadedFileName('');
                      }}
                      className="p-1.5 hover:bg-rose-500/10 rounded-lg text-slate-400 hover:text-rose-400 transition-colors"
                      title="Remove & Re-upload"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative border-2 border-dashed border-slate-800 hover:border-indigo-500/50 rounded-xl p-5 transition-colors text-center bg-slate-950/50 group">
                  <input
                    type="file"
                    id="kyc-file-upload"
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                  <label
                    htmlFor="kyc-file-upload"
                    className={`cursor-pointer flex flex-col items-center justify-center gap-2.5 ${
                      isUploading ? 'opacity-50 cursor-wait' : ''
                    }`}
                  >
                    {isUploading ? (
                      <Loader2 className="w-7 h-7 text-indigo-400 animate-spin" />
                    ) : (
                      <div className="w-11 h-11 rounded-2xl bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-105 transition-all">
                        <Upload className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-bold text-slate-200">
                        {isUploading ? 'Uploading file to secure storage...' : 'Click to select & upload document'}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Supports PDF, PNG, JPG, JPEG, DOC up to 15MB
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
              {isRejected && (
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-400 hover:text-slate-200 cursor-pointer"
                >
                  Cancel Edit
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-slate-200 cursor-pointer"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading || isUploading || !documentUrl}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-indigo-600/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Submit Document for Verification</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
